import { subDays, startOfDay, endOfDay, parseISO, format, isWithinInterval, startOfWeek, startOfMonth, eachWeekOfInterval, eachMonthOfInterval, subWeeks, subMonths, endOfWeek, endOfMonth } from 'date-fns';

/**
 * Filter records by date range
 */
export function filterByDateRange(records, startDate, endDate) {
  if (!records || !Array.isArray(records)) return [];
  
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  
  return records.filter(record => {
    if (!record.date) return false;
    let recordDate;
    try {
      if (typeof record.date === 'string') {
        // Try ISO format first, then fallback to Date constructor
        try {
          recordDate = parseISO(record.date);
        } catch {
          recordDate = new Date(record.date);
        }
      } else {
        recordDate = new Date(record.date);
      }
      if (isNaN(recordDate.getTime())) return false;
      return isWithinInterval(recordDate, { start, end });
    } catch {
      return false;
    }
  });
}

/**
 * Get date range based on period selection or custom date range
 */
export function getDateRangeForPeriod(period, customDateRange = null) {
  // If custom date range is provided, use it
  if (customDateRange && customDateRange.start && customDateRange.end) {
    return {
      start: startOfDay(new Date(customDateRange.start)),
      end: endOfDay(new Date(customDateRange.end))
    };
  }

  // Otherwise use preset periods
  const today = new Date();
  let startDate;
  let endDate;
  
  if (period === '7') {
    startDate = subDays(today, 7);
    endDate = today;
  } else if (period === 'lastWeek') {
    // Previous calendar week (Monday to Sunday)
    const lastWeekStart = subWeeks(today, 1);
    startDate = startOfWeek(lastWeekStart, { weekStartsOn: 1 });
    endDate = endOfWeek(lastWeekStart, { weekStartsOn: 1 });
  } else if (period === '30') {
    startDate = subDays(today, 30);
    endDate = today;
  } else if (period === 'lastMonth') {
    // Previous calendar month (first day to last day)
    const lastMonthStart = subMonths(today, 1);
    startDate = startOfMonth(lastMonthStart);
    endDate = endOfMonth(lastMonthStart);
  } else if (period === '90') {
    startDate = subDays(today, 90);
    endDate = today;
  } else {
    // Invalid period
    return null;
  }
  
  return {
    start: startOfDay(startDate),
    end: endOfDay(endDate)
  };
}

/**
 * Group records by theme and calculate counts
 */
export function groupByTheme(records) {
  const themeGroups = {};
  
  records.forEach(record => {
    const themeId = record.theme || 'uncategorized';
    const themeName = record.themeName || 'Uncategorized';
    
    if (!themeGroups[themeId]) {
      themeGroups[themeId] = {
        themeId,
        name: themeName,
        count: 0,
        records: []
      };
    }
    
    themeGroups[themeId].count++;
    themeGroups[themeId].records.push(record);
  });
  
  return Object.values(themeGroups).sort((a, b) => b.count - a.count);
}

/**
 * Calculate percentages for themes
 */
export function addPercentages(themeGroups, total) {
  return themeGroups.map(group => ({
    ...group,
    percentage: total > 0 ? ((group.count / total) * 100).toFixed(1) : 0
  }));
}

/**
 * Group records by sub-theme and calculate counts
 * Only includes records that have a sub-theme assigned
 */
export function groupBySubTheme(records) {
  const subThemeGroups = {};
  
  records.forEach(record => {
    // Only include records with sub-theme assigned
    if (!record.subTheme || !record.subThemeName) return;
    
    const subThemeId = record.subTheme;
    const subThemeName = record.subThemeName;
    
    if (!subThemeGroups[subThemeId]) {
      subThemeGroups[subThemeId] = {
        subThemeId,
        name: subThemeName,
        count: 0,
        records: []
      };
    }
    
    subThemeGroups[subThemeId].count++;
    subThemeGroups[subThemeId].records.push(record);
  });
  
  return Object.values(subThemeGroups).sort((a, b) => b.count - a.count);
}

/**
 * Generate daily time series data for a theme
 */
export function generateDailyTimeSeries(records, startDate, endDate) {
  const dailyData = {};
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  
  // Initialize all days in range with 0
  let currentDate = new Date(start);
  while (currentDate <= end) {
    const dateKey = format(currentDate, 'yyyy-MM-dd');
    dailyData[dateKey] = {
      date: dateKey,
      count: 0,
      displayDate: format(currentDate, 'MMM dd')
    };
    currentDate = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Count records per day
  records.forEach(record => {
    if (!record.date) return;
    try {
      let recordDate;
      if (typeof record.date === 'string') {
        try {
          recordDate = parseISO(record.date);
        } catch {
          recordDate = new Date(record.date);
        }
      } else {
        recordDate = new Date(record.date);
      }
      if (isNaN(recordDate.getTime())) return;
      const dateKey = format(recordDate, 'yyyy-MM-dd');
      
      if (dailyData[dateKey]) {
        dailyData[dateKey].count++;
      }
    } catch {
      // Skip invalid dates
    }
  });
  
  return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Generate weekly time series data for a theme
 */
export function generateWeeklyTimeSeries(records, startDate, endDate) {
  const weeklyData = {};
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  
  // Get all weeks in the range
  const weeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 }); // Monday as start of week
  
  // Initialize all weeks in range with 0
  weeks.forEach(weekStart => {
    const weekKey = format(weekStart, 'yyyy-MM-dd');
    weeklyData[weekKey] = {
      date: weekKey,
      count: 0,
      displayDate: `Week of ${format(weekStart, 'MMM dd')}`
    };
  });
  
  // Count records per week
  records.forEach(record => {
    if (!record.date) return;
    try {
      let recordDate;
      if (typeof record.date === 'string') {
        try {
          recordDate = parseISO(record.date);
        } catch {
          recordDate = new Date(record.date);
        }
      } else {
        recordDate = new Date(record.date);
      }
      if (isNaN(recordDate.getTime())) return;
      
      // Find which week this record belongs to
      const weekStart = startOfWeek(recordDate, { weekStartsOn: 1 });
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      
      if (weeklyData[weekKey]) {
        weeklyData[weekKey].count++;
      }
    } catch {
      // Skip invalid dates
    }
  });
  
  return Object.values(weeklyData).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Generate monthly time series data for a theme
 */
export function generateMonthlyTimeSeries(records, startDate, endDate) {
  const monthlyData = {};
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  
  // Get all months in the range
  const months = eachMonthOfInterval({ start, end });
  
  // Initialize all months in range with 0
  months.forEach(monthStart => {
    const monthKey = format(monthStart, 'yyyy-MM');
    monthlyData[monthKey] = {
      date: monthKey,
      count: 0,
      displayDate: format(monthStart, 'MMM yyyy')
    };
  });
  
  // Count records per month
  records.forEach(record => {
    if (!record.date) return;
    try {
      let recordDate;
      if (typeof record.date === 'string') {
        try {
          recordDate = parseISO(record.date);
        } catch {
          recordDate = new Date(record.date);
        }
      } else {
        recordDate = new Date(record.date);
      }
      if (isNaN(recordDate.getTime())) return;
      
      // Find which month this record belongs to
      const monthStart = startOfMonth(recordDate);
      const monthKey = format(monthStart, 'yyyy-MM');
      
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].count++;
      }
    } catch {
      // Skip invalid dates
    }
  });
  
  return Object.values(monthlyData).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Prepare data for bar chart
 */
export function prepareBarChartData(themeGroups) {
  const filteredGroups = themeGroups.filter(
    group => group.name !== "Uncategorized" && group.themeId !== "uncategorized"
  );
  return filteredGroups.map((group, index) => ({
    name: group.name,
    value: group.count,
    percentage: group.percentage,
    fill: getThemeColor(index)
  }));
}

/**
 * Prepare data for pie chart
 */
export function preparePieChartData(themeGroups) {
  const filteredGroups = themeGroups.filter(
    group => group.name !== "Uncategorized" && group.themeId !== "uncategorized"
  );
  return filteredGroups.map((group, index) => ({
    name: group.name,
    value: group.count,
    percentage: group.percentage,
    fill: getThemeColor(index)
  }));
}

/**
 * Prepare data for sub-theme bar chart
 */
export function prepareSubThemeChartData(subThemeGroups) {
  return subThemeGroups.map((group, index) => ({
    subThemeId: group.subThemeId,
    name: group.name,
    value: group.count,
    percentage: group.percentage,
    fill: getThemeColor(index)
  }));
}

/**
 * Get color for theme (cycling through CSS variables)
 */
function getThemeColor(index) {
  const colorIndex = (index % 20) + 1;
  return `var(--chartColor${colorIndex})`;
}

/**
 * Filter records by brand
 */
export function filterByBrand(records, brandId) {
  if (!records || !Array.isArray(records)) return [];
  if (!brandId) return records; // Return all if no brand filter
  
  return records.filter(record => record.brand === brandId);
}

/**
 * Filter records by source (feedback type)
 */
export function filterBySource(records, source) {
  if (!records || !Array.isArray(records)) return [];
  if (!source) return records; // Return all if no source filter
  
  return records.filter(record => record.source === source);
}

/**
 * Filter records by country
 */
export function filterByCountry(records, countryId) {
  if (!records || !Array.isArray(records)) return [];
  if (!countryId) return records; // Return all if no country filter
  
  return records.filter(record => record.country === countryId);
}

/**
 * Filter records by sentiment
 */
export function filterBySentiment(records, selectedSentiments) {
  if (!records || !Array.isArray(records)) return [];
  if (!selectedSentiments || !Array.isArray(selectedSentiments) || selectedSentiments.length === 0) {
    return records; // Return all if no sentiment filter
  }
  
  return records.filter(record => {
    if (!record.sentiment) return false;
    return selectedSentiments.includes(record.sentiment);
  });
}

/**
 * Filter records by sub-theme
 */
export function filterBySubTheme(records, subThemeId) {
  if (!records || !Array.isArray(records)) return [];
  if (!subThemeId) return records; // Return all if no sub-theme filter
  
  return records.filter(record => record.subTheme === subThemeId);
}

/**
 * Get all records from all uploads
 */
export function getAllRecords(uploads) {
  if (!uploads || !Array.isArray(uploads)) return [];
  
  return uploads.reduce((allRecords, upload) => {
    if (upload.records) {
      // Handle both array and object formats
      if (Array.isArray(upload.records)) {
        // Attach uploadId to each record
        const recordsWithUploadId = upload.records.map(record => ({
          ...record,
          uploadId: upload.id
        }));
        return [...allRecords, ...recordsWithUploadId];
      } else if (typeof upload.records === 'object') {
        // Convert object to array and attach uploadId
        const recordsArray = Object.keys(upload.records).map(id => ({
          id,
          ...upload.records[id],
          uploadId: upload.id
        }));
        return [...allRecords, ...recordsArray];
      }
    }
    return allRecords;
  }, []);
}

/**
 * Calculate average rating from records
 * Returns object with average, count, and formatted string
 */
export function calculateAverageRating(records) {
  if (!records || !Array.isArray(records)) {
    return { average: null, count: 0, formatted: null };
  }

  // Filter records that have ratings and convert to numbers
  const ratings = records
    .filter(record => record.rating !== null && record.rating !== undefined && record.rating !== '')
    .map(record => {
      const rating = record.rating;
      // Handle string ratings (e.g., "4.5", "4/5", "80%")
      if (typeof rating === 'string') {
        // Try to extract number from string (handles "4/5", "4.5", "80%", etc.)
        const match = rating.match(/(\d+\.?\d*)/);
        if (match) {
          let num = parseFloat(match[1]);
          // If it looks like a percentage (e.g., "80%"), convert to 0-5 scale
          if (rating.includes('%')) {
            num = (num / 100) * 5;
          }
          // If it's out of 10, convert to 0-5 scale
          else if (num > 5 && num <= 10) {
            num = (num / 10) * 5;
          }
          return num;
        }
        return null;
      }
      // Handle numeric ratings
      if (typeof rating === 'number') {
        // If rating is out of 10, convert to 0-5 scale
        if (rating > 5 && rating <= 10) {
          return (rating / 10) * 5;
        }
        return rating;
      }
      return null;
    })
    .filter(rating => rating !== null && !isNaN(rating) && rating >= 0 && rating <= 5);

  if (ratings.length === 0) {
    return { average: null, count: 0, formatted: null };
  }

  const sum = ratings.reduce((acc, rating) => acc + rating, 0);
  const average = sum / ratings.length;
  const formatted = `${average.toFixed(1)}/5`;

  return {
    average: average,
    count: ratings.length,
    formatted: formatted
  };
}

/**
 * Calculate sentiment summary from records
 * Returns object with counts and percentages for each sentiment type
 */
export function calculateSentimentSummary(records) {
  if (!records || !Array.isArray(records)) {
    return {
      positive: { count: 0, percentage: 0 },
      negative: { count: 0, percentage: 0 },
      neutral: { count: 0, percentage: 0 },
      total: 0
    };
  }

  // Filter out uncategorized records and records that have sentiment
  const recordsWithSentiment = records.filter(record => {
    // Exclude uncategorized records
    const themeId = record.theme || 'uncategorized';
    const themeName = record.themeName || 'Uncategorized';
    const isUncategorized = themeId === 'uncategorized' || themeName === 'Uncategorized' || !record.theme;
    
    // Include only records with sentiment that are not uncategorized
    return !isUncategorized && record.sentiment && (record.sentiment === 'positive' || record.sentiment === 'negative' || record.sentiment === 'neutral');
  });

  if (recordsWithSentiment.length === 0) {
    return {
      positive: { count: 0, percentage: 0 },
      negative: { count: 0, percentage: 0 },
      neutral: { count: 0, percentage: 0 },
      total: 0
    };
  }

  // Count by sentiment
  const counts = {
    positive: 0,
    negative: 0,
    neutral: 0
  };

  recordsWithSentiment.forEach(record => {
    if (record.sentiment === 'positive') {
      counts.positive++;
    } else if (record.sentiment === 'negative') {
      counts.negative++;
    } else if (record.sentiment === 'neutral') {
      counts.neutral++;
    }
  });

  const total = recordsWithSentiment.length;

  return {
    positive: {
      count: counts.positive,
      percentage: total > 0 ? ((counts.positive / total) * 100).toFixed(1) : 0
    },
    negative: {
      count: counts.negative,
      percentage: total > 0 ? ((counts.negative / total) * 100).toFixed(1) : 0
    },
    neutral: {
      count: counts.neutral,
      percentage: total > 0 ? ((counts.neutral / total) * 100).toFixed(1) : 0
    },
    total: total
  };
}

/**
 * Check if two records are duplicates based on key fields
 */
export function isDuplicateRecord(record1, record2) {
  // Normalize dates for comparison
  const normalizeDate = (date) => {
    if (!date) return null;
    try {
      const d = new Date(date);
      return isNaN(d.getTime()) ? null : d.toISOString().split('T')[0]; // YYYY-MM-DD
    } catch {
      return null;
    }
  };

  // Normalize text for comparison (trim, lowercase)
  const normalizeText = (text) => {
    return text ? text.trim().toLowerCase() : '';
  };

  // Compare key fields
  const date1 = normalizeDate(record1.date);
  const date2 = normalizeDate(record2.date);
  
  if (date1 && date2 && date1 !== date2) return false;
  if (!date1 || !date2) return false; // Both must have dates

  // Compare feedback text (if both have it)
  const text1 = normalizeText(record1.feedbackText);
  const text2 = normalizeText(record2.feedbackText);
  if (text1 && text2 && text1 !== text2) return false;

  // Compare rating (if both have it)
  if (record1.rating && record2.rating && record1.rating !== record2.rating) return false;

  // Compare userId (if both have it)
  if (record1.userId && record2.userId && record1.userId !== record2.userId) return false;

  // Compare source, brand, country (must match)
  if (record1.source !== record2.source) return false;
  if (record1.brand !== record2.brand) return false;
  if (record1.country !== record2.country) return false;

  // If we get here, records match on all key fields
  return true;
}

/**
 * Filter out duplicate records, preserving existing ones
 * Returns: { newRecords: [], duplicates: [], summary: { total, new, duplicates } }
 */
export function filterDuplicates(newRecords, existingRecords) {
  const duplicates = [];
  const newRecordsFiltered = [];

  newRecords.forEach(newRecord => {
    const isDuplicate = existingRecords.some(existingRecord => 
      isDuplicateRecord(newRecord, existingRecord)
    );

    if (isDuplicate) {
      duplicates.push(newRecord);
    } else {
      newRecordsFiltered.push(newRecord);
    }
  });

  return {
    newRecords: newRecordsFiltered,
    duplicates: duplicates,
    summary: {
      total: newRecords.length,
      new: newRecordsFiltered.length,
      duplicates: duplicates.length
    }
  };
}

